// src/services/condoService.ts
// API service for condo operations

import {
  Condo,
  CreateCondoData,
  UpdateCondoData,
  CondoWaitlistEntry,
  GetWaitlistUsersResponse,
  CondoPagination,
} from '../types/condo';
import { ApiResponse } from './apiService';
import { DiscoverFilters as DiscoverFiltersType } from '../types/discover';

/** Location-only filter subset passed by CondosPage. */
export type CondoLocationFilters = Pick<DiscoverFiltersType, 'locations' | 'neighborhoodIds'>;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class CondoService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}/${endpoint.startsWith('/') ? endpoint.slice(1) : endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        throw new Error('Invalid JSON response from server');
      }

      if (!response.ok) {
        const error: any = new Error(data.message || `HTTP error! status: ${response.status}`);
        error.response = { data };
        throw error;
      }

      return data;
    } catch (error) {
      console.error('❌ Condo API Request Error:', error);
      throw error;
    }
  }

  /**
   * List condos with optional pagination and location filters (authenticated users).
   * Returns condos and pagination; each condo has isOnWaitlist for current user.
   */
  async listCondos(
    page: number = 1,
    limit: number = 20,
    filters?: CondoLocationFilters
  ): Promise<ApiResponse<{ condos: Condo[]; pagination: CondoPagination }>> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });

    if (filters?.locations && filters.locations.length > 0) {
      filters.locations.forEach(borough => params.append('borough', borough));
    }
    if (filters?.neighborhoodIds && filters.neighborhoodIds.length > 0) {
      // Preserve the full "borough-neighbourhood" prefix — backend needs it for per-borough grouping
      filters.neighborhoodIds.forEach(id => params.append('neighborhood', id));
    }

    return this.request<{ condos: Condo[]; pagination: CondoPagination }>(
      `condos?${params}`,
      { method: 'GET' }
    );
  }

  /**
   * Join condo waitlist — one-click, no unit-type selection.
   */
  async joinWaitlist(
    condoId: string
  ): Promise<ApiResponse<{ waitlistEntry: { condoId: string; userId: string }; condo: { waitlistCount: number } }>> {
    return this.request(`condos/${condoId}/waitlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify({}),
    });
  }

  /**
   * Leave condo waitlist.
   */
  async leaveWaitlist(
    condoId: string
  ): Promise<ApiResponse<{ condo: { waitlistCount: number } }>> {
    return this.request(`condos/${condoId}/waitlist`, { method: 'DELETE' });
  }

  /**
   * Get current user's waitlist entries.
   */
  async getWaitlistEntries(): Promise<ApiResponse<{ entries: CondoWaitlistEntry[] }>> {
    return this.request<{ entries: CondoWaitlistEntry[] }>('condos/waitlist', {
      method: 'GET',
    });
  }

  /**
   * Get users on condo waitlist (for waitlist modal).
   * Excludes declined; includes hasConversation, isPending per user.
   */
  async getWaitlistUsers(condoId: string): Promise<ApiResponse<GetWaitlistUsersResponse>> {
    return this.request<GetWaitlistUsersResponse>(`condos/${condoId}/waitlist/users`, {
      method: 'GET',
    });
  }

  /**
   * List all draft condos (admin only).
   */
  async listDraftCondos(): Promise<ApiResponse<{ condos: Condo[] }>> {
    return this.request<{ condos: Condo[] }>('condos/drafts', { method: 'GET' });
  }

  /**
   * Get condo by ID
   */
  async getCondoById(condoId: string): Promise<ApiResponse<{ condo: Condo }>> {
    return this.request<{ condo: Condo }>(`condos/${condoId}`, {
      method: 'GET',
    });
  }

  /**
   * Get condo by short ID (for short URL redirect).
   */
  async getCondoByShortId(shortId: string): Promise<ApiResponse<{ condo: Condo }>> {
    return this.request<{ condo: Condo }>(`condos/s/${shortId}`, {
      method: 'GET',
    });
  }

  /**
   * Create a new condo
   */
  async createCondo(
    condoData: CreateCondoData,
    imageFiles?: File[]
  ): Promise<ApiResponse<{ condo: Condo }>> {
    const formData = new FormData();
    
    // Send JSON data as string (multer doesn't handle nested FormData well)
    formData.append('data', JSON.stringify(condoData));
    
    // Add images if provided
    if (imageFiles && imageFiles.length > 0) {
      imageFiles.forEach((file) => {
        formData.append('images', file);
      });
    }

    return this.request<{ condo: Condo }>('condos', {
      method: 'POST',
      headers: {
        // Don't set Content-Type for FormData, browser will set it with boundary
        ...this.getAuthHeaders(),
      },
      body: formData,
    });
  }

  /**
   * Submit a condo for admin review (user-facing).
   * Sends FormData with data as JSON string + optional image files.
   */
  async submitCondo(
    data: CreateCondoData,
    imageFiles?: File[]
  ): Promise<ApiResponse<{ condo: Condo }>> {
    const formData = new FormData();

    // Send JSON data as string (multer doesn't handle nested FormData well)
    formData.append('data', JSON.stringify(data));

    // Add images if provided
    if (imageFiles && imageFiles.length > 0) {
      imageFiles.forEach((file) => {
        formData.append('images', file);
      });
    }

    return this.request<{ condo: Condo }>('condos/submit', {
      method: 'POST',
      headers: {
        // Don't set Content-Type for FormData, browser will set it with boundary
        ...this.getAuthHeaders(),
      },
      body: formData,
    });
  }

  /**
   * Get current user's submitted condos.
   */
  async getMySubmissions(): Promise<ApiResponse<{ condos: Condo[] }>> {
    return this.request<{ condos: Condo[] }>('condos/my-submissions', { method: 'GET' });
  }

  /**
   * Withdraw a pending user submission (reverts to draft).
   */
  async withdrawSubmission(condoId: string): Promise<ApiResponse<{ condo: Condo }>> {
    return this.request<{ condo: Condo }>(`condos/${condoId}/withdraw`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify({}),
    });
  }

  /**
   * User updates their own condo submission (draft, pending, or declined).
   * Sends FormData with data as JSON string + optional image files.
   */
  async updateUserSubmission(
    condoId: string,
    data: UpdateCondoData,
    imageFiles?: File[]
  ): Promise<ApiResponse<{ condo: Condo }>> {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));
    imageFiles?.forEach((f) => formData.append('images', f));
    return this.request<{ condo: Condo }>(`condos/${condoId}/user-update`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders() },
      body: formData,
    });
  }

  /**
   * Get all pending condos (admin queue).
   */
  async getPendingCondos(): Promise<ApiResponse<{ condos: Condo[] }>> {
    return this.request<{ condos: Condo[] }>('condos/pending', { method: 'GET' });
  }

  /**
   * Admin approves or declines a submitted condo.
   */
  async reviewCondo(
    condoId: string,
    action: 'approve' | 'decline',
    reason?: string
  ): Promise<ApiResponse<{ condo: Condo }>> {
    return this.request<{ condo: Condo }>(`condos/${condoId}/review`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify({ action, reason }),
    });
  }

  /**
   * Update an existing condo
   */
  async updateCondo(
    condoId: string,
    condoData: UpdateCondoData,
    imageFiles?: File[]
  ): Promise<ApiResponse<{ condo: Condo }>> {
    const formData = new FormData();
    
    // Send JSON data as string (multer doesn't handle nested FormData well)
    formData.append('data', JSON.stringify(condoData));
    
    // Add images if provided
    if (imageFiles && imageFiles.length > 0) {
      imageFiles.forEach((file) => {
        formData.append('images', file);
      });
    }

    return this.request<{ condo: Condo }>(`condos/${condoId}`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
      },
      body: formData,
    });
  }
}

export const condoService = new CondoService();
